import React from 'react';

import { QUIZ_LIMITS_REACHED_ERROR } from '@showbie-socrative/socrative-utils';
import { translate } from '~l10n/client';
import { AxiosError } from 'axios';
import cx from 'classnames';

import { PaywallScreens } from '~analytics/types';
import { UIButton } from '~shared/components/buttons';
import { Dialog } from '~shared/components/Dialog/Dialog';
import { Divider } from '~shared/components/Divider';
import { useAutoFocus } from '~shared/components/helpers';
import { Inline } from '~shared/components/Inline';
import TextInput from '~shared/components/TextInput';
import { PaywallDispatch } from '~teacher/components/PaywallDialog';
import { useCopyQuizBySoc } from '~teacher/hook/useCopyQuizBySoc';

interface CopyDialogProps {
  isOpen: boolean;
  title: string;
  originalName: string;
  parentId: number;
  socNumber: string;
  closeDialog: () => void;
  closeLabel?: string;
  paywallDispatch: PaywallDispatch;
}

function useResetModal({
  originalName,
  isOpen,
  setNewName,
  setDisableCopy,
}: {
  originalName: string;
  isOpen: boolean;
  setNewName: React.Dispatch<React.SetStateAction<string>>;
  setDisableCopy: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  React.useEffect(() => {
    if (!isOpen) return;
    setNewName(`${originalName} (${translate('Copy')})`);
    setDisableCopy(false);
  }, [originalName, isOpen, setNewName, setDisableCopy]);
}

const CopyQuizDialog = ({
  isOpen,
  title,
  originalName,
  parentId,
  socNumber,
  closeDialog,
  closeLabel,
  paywallDispatch,
}: CopyDialogProps) => {
  const [newName, setNewName] = React.useState<string>();
  const [disableCopy, setDisableCopy] = React.useState(false);

  useResetModal({
    originalName: originalName,
    isOpen: isOpen,
    setNewName: setNewName,
    setDisableCopy: setDisableCopy,
  });

  const copyQuizBySoc = useCopyQuizBySoc([originalName, newName]);

  const onSuccess = (onClose: () => void) => () => {
    onClose();
  };

  const onError =
    (paywallDispatch: PaywallDispatch, onClose: () => void) =>
    (error: unknown) => {
      if ((error as any).isAxiosError) {
        const errorData = (error as AxiosError).response.data;
        if (errorData.code === QUIZ_LIMITS_REACHED_ERROR.code) {
          onClose();
          paywallDispatch({
            type: 'OPEN',
            touch: [
              PaywallScreens.QuizListLimitReached,
              PaywallScreens.QuizListCopyQuiz,
            ],
          });
        }
      }
    };

  const copyBySocNumber = () => {
    setDisableCopy(true);
    copyQuizBySoc.mutate(
      { parentId, socNumber, newName, originalName },
      {
        onSuccess: onSuccess(handleClose),
        onError: onError(paywallDispatch, handleClose),
      }
    );
    setNewName('');
  };

  const newNameInputClass = cx('w-26 mt-3', {
    'border-red-800': copyQuizBySoc.isError,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewName(e.target.value);
  };

  const handleClose = () => {
    setNewName('');
    closeDialog();
  };

  const autoFocusOnOpenCallback = useAutoFocus();

  return (
    <Dialog
      id="copy-quiz-dialog"
      open={isOpen}
      onClose={handleClose}
      title={title}
      closeLabel={closeLabel}
    >
      <div className="mt-5 mb-5">
        <Divider />
      </div>
      <div className="text-base leading-8">
        <label htmlFor="new-quiz-name-input">{translate('Quiz Title')}</label>
        <TextInput
          ref={autoFocusOnOpenCallback}
          value={newName}
          id="new-quiz-name-input"
          withBorder
          className={newNameInputClass}
          onChange={handleChange}
        />
      </div>
      <div className="pt-8">
        <Inline align="right">
          <UIButton isDisabled={copyQuizBySoc.isLoading} onClick={handleClose}>
            {translate('Cancel')}
          </UIButton>
          <UIButton
            onClick={copyBySocNumber}
            isDisabled={disableCopy}
            appearance="primary"
          >
            {translate('Duplicate')}
          </UIButton>
        </Inline>
      </div>
    </Dialog>
  );
};

export { CopyQuizDialog };
